import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import { css, keyframes } from '@emotion/css';

import { BotonSecundario, Hoja } from './InicioRumbo';
import { ATARDECER, CIELO, PuertoAtardecer } from './PuertoAtardecer';

/** Dónde guarda este aparato su turno, para enseñarlo al volver a abrir. */
const CLAVE_ESPERA = 'rumbo-lista-espera';
/** Cada cuánto se mira si ya hay sitio mientras la pantalla está abierta. */
const REFRESCO_MS = 60_000;

export type EstadoEspera = {
  turno: number;
  correo: string;
  nombre: string | null;
  delante: number;
  novedades: boolean;
  haySitio: boolean;
};

export function leerEspera(): string | null {
  try {
    return window.localStorage.getItem(CLAVE_ESPERA);
  } catch {
    return null;
  }
}

export function guardarEspera(id: string | null) {
  try {
    if (id) {
      window.localStorage.setItem(CLAVE_ESPERA, id);
    } else {
      window.localStorage.removeItem(CLAVE_ESPERA);
    }
  } catch {
    // Sin almacenamiento: al volver a abrir saldrá la entrada normal.
  }
}

async function pedir(
  id: string,
  cuerpo?: { si: boolean },
): Promise<EstadoEspera | 'no-existe' | null> {
  const url = await send('get-server-url');
  if (!url) {
    return null;
  }
  try {
    const r = await fetch(
      `${url}/rumbo/espera/${encodeURIComponent(id)}${cuerpo ? '/novedades' : ''}`,
      cuerpo
        ? {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cuerpo),
          }
        : undefined,
    );
    if (r.status === 404) {
      return 'no-existe';
    }
    const json = (await r.json()) as { data?: EstadoEspera };
    return json.data ?? null;
  } catch {
    return null;
  }
}

type ListaEsperaProps = {
  id: string;
  /** Volver a la entrada para probar con otro Gmail. */
  onOtraCuenta: () => void;
  /** Ya hay sitio: entrar con Google. */
  onSubir: () => void | Promise<void>;
};

/**
 * Lista de espera (Vadik, 30-oct-2026): quien entra con Google cuando no
 * quedan plazas ve su turno y cuántas personas tiene delante, sin errores
 * técnicos. Cuando el admin sube las plazas desde el Puente y le toca, la
 * misma pantalla le dice que ya hay sitio. La casilla de novedades sale sin
 * marcar: los avisos de servicio no necesitan permiso, la publicidad sí.
 */
export function ListaEspera({ id, onOtraCuenta, onSubir }: ListaEsperaProps) {
  const { t } = useTranslation();
  const [estado, setEstado] = useState<EstadoEspera | null>(null);
  const [sinConexion, setSinConexion] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  useEffect(() => {
    let vivo = true;
    const cargar = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      void pedir(id).then(r => {
        if (!vivo) {
          return;
        }
        if (r === 'no-existe') {
          guardarEspera(null);
          onOtraCuenta();
        } else if (r) {
          setEstado(r);
          setSinConexion(false);
        } else {
          setSinConexion(true);
        }
      });
    };
    cargar();
    const reloj = window.setInterval(cargar, REFRESCO_MS);
    document.addEventListener('visibilitychange', cargar);
    return () => {
      vivo = false;
      window.clearInterval(reloj);
      document.removeEventListener('visibilitychange', cargar);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function cambiarNovedades() {
    if (!estado) {
      return;
    }
    const si = !estado.novedades;
    setEstado({ ...estado, novedades: si });
    const r = await pedir(id, { si });
    if (!r) {
      setEstado(e => (e ? { ...e, novedades: !si } : e));
    }
  }

  async function subir() {
    setSubiendo(true);
    try {
      await onSubir();
    } finally {
      setSubiendo(false);
    }
  }

  const haySitio = estado?.haySitio ?? false;
  const nombre = estado?.nombre;

  return (
    <View
      data-testid="lista-espera"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 6000,
        background: CIELO,
        color: ATARDECER.tinta,
        overflow: 'hidden',
        alignItems: 'center',
      }}
    >
      <View
        style={{
          position: 'relative',
          flex: 1,
          width: '100%',
          maxWidth: 480,
          justifyContent: 'flex-end',
        }}
      >
        <View style={{ flex: 1, minHeight: 140, justifyContent: 'flex-end' }}>
          <PuertoAtardecer
            key={haySitio ? 'amarre' : 'lleno'}
            variante={haySitio ? 'amarre' : 'lleno'}
            style={{ height: '100%', minHeight: 140 }}
          />
          {estado && !haySitio && <Tique turno={estado.turno} />}
        </View>
        <Hoja alinear="stretch">
          <span style={estiloEyebrow}>
            {haySitio ? t('You have a berth') : t('Waiting list')}
          </span>
          <h2 style={estiloTitulo} data-testid="lista-espera-titulo">
            {haySitio
              ? nombre
                ? t("There's room for you, {{name}}", { name: nombre })
                : t("There's room for you")
              : nombre
                ? t("I'm saving your place, {{name}}", { name: nombre })
                : t("I'm saving your place")}
          </h2>
          {!estado ? (
            <p style={estiloTexto}>
              {sinConexion
                ? t("Can't reach Rumbo right now. Check your connection.")
                : t('Looking for your turn…')}
            </p>
          ) : haySitio ? (
            <>
              <p style={estiloTexto}>
                <Trans>
                  Your place is ready. First you'll create your budget, and I'll
                  guide you step by step.
                </Trans>
              </p>
              <Bocadillo>
                <Trans>
                  I saved your place. It wasn't easy: there was a very insistent
                  squid.
                </Trans>
              </Bocadillo>
            </>
          ) : (
            <>
              <p style={estiloTexto}>
                <Trans
                  i18nKey="Right now the harbour is full: we let people in little by little so everything runs smoothly. When a berth frees up, you'll see it here and we'll write to <b>{{email}}</b>."
                  values={{ email: estado.correo }}
                  components={{
                    b: <b style={{ color: ATARDECER.tinta }} />,
                  }}
                />
              </p>
              <Bocadillo>
                <Trans>
                  It's a short queue. I know because I've cut in twice to check.
                </Trans>
              </Bocadillo>
              <View
                data-testid="lista-espera-delante"
                style={{
                  marginTop: 12,
                  background: ATARDECER.papel2,
                  borderRadius: 16,
                  padding: '10px 12px',
                  gap: 6,
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>
                  {estado.delante === 0
                    ? t("You're next")
                    : t('You have {{count}} people ahead', {
                        count: estado.delante,
                      })}
                </span>
                <View
                  aria-hidden
                  style={{
                    height: 6,
                    borderRadius: 3,
                    background: '#e6dcc6',
                    overflow: 'hidden',
                  }}
                >
                  <View
                    style={{
                      height: '100%',
                      width: `${Math.max(8, 100 / (estado.delante + 1))}%`,
                      background: ATARDECER.laton,
                      borderRadius: 3,
                      transition: 'width 1.2s cubic-bezier(.2,.8,.2,1)',
                    }}
                  />
                </View>
              </View>
              <label
                style={{
                  marginTop: 12,
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: ATARDECER.tinta2,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  data-testid="lista-espera-novedades"
                  checked={estado.novedades}
                  onChange={() => void cambiarNovedades()}
                  style={{
                    width: 18,
                    height: 18,
                    margin: '1px 0 0',
                    accentColor: ATARDECER.verde,
                    flexShrink: 0,
                  }}
                />
                <Trans>
                  Write to me as well when there's news about Rumbo.
                </Trans>
              </label>
            </>
          )}
          <View style={{ marginTop: 18, alignItems: 'center' }}>
            {haySitio && (
              <button
                type="button"
                data-testid="lista-espera-subir"
                onClick={() => void subir()}
                disabled={subiendo}
                className={css(estiloBotonVerde)}
              >
                <Trans>Come aboard</Trans>
              </button>
            )}
            <BotonSecundario
              testid="lista-espera-otra-cuenta"
              onClick={() => {
                guardarEspera(null);
                onOtraCuenta();
              }}
            >
              <Trans>Sign in with another account</Trans>
            </BotonSecundario>
          </View>
        </Hoja>
      </View>
    </View>
  );
}

/** Lo que dice Gavi desde el muelle (bromas aprobadas por Vadik, 30-oct). */
function Bocadillo({ children }: { children: ReactNode }) {
  return (
    <p data-testid="lista-espera-gavi" className={estiloBocadillo}>
      {children}
    </p>
  );
}

/** El tique con el turno que Gavi sella al llegar. */
function Tique({ turno }: { turno: number }) {
  return (
    <View
      aria-hidden
      className={estiloTique}
      style={{
        position: 'absolute',
        left: '50%',
        top: '12%',
        width: 132,
        marginLeft: -66,
        height: 70,
        borderRadius: 8,
        background: '#fffaf0',
        boxShadow: '0 10px 22px -10px rgba(20,46,53,.6)',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span
        style={{
          fontSize: 8.5,
          fontWeight: 800,
          letterSpacing: '.16em',
          textTransform: 'uppercase',
          color: ATARDECER.tinta3,
        }}
      >
        <Trans>Your turn</Trans>
      </span>
      <b
        style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: 30,
          lineHeight: 1.1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {turno}
      </b>
      <span className={estiloSello}>
        <Trans>Place saved</Trans>
      </span>
    </View>
  );
}

const llegaTique = keyframes({
  from: { opacity: 0, transform: 'translateY(26px) rotate(6deg)' },
  to: { opacity: 1, transform: 'rotate(-4deg)' },
});
const sella = keyframes({
  from: { opacity: 0, transform: 'rotate(14deg) scale(2.2)' },
  to: { opacity: 1, transform: 'rotate(-10deg) scale(1)' },
});

const estiloTique = css({
  transform: 'rotate(-4deg)',
  animation: `${llegaTique} .7s cubic-bezier(.2,.8,.2,1) .2s both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const asoma = keyframes({
  from: { opacity: 0, transform: 'translateY(6px)' },
  to: { opacity: 1, transform: 'none' },
});

const estiloBocadillo = css({
  position: 'relative',
  margin: '12px 0 0',
  padding: '9px 12px',
  borderRadius: 14,
  background: '#fff',
  boxShadow: '0 6px 16px -10px rgba(20,46,53,.5)',
  fontSize: 13.5,
  fontWeight: 600,
  lineHeight: 1.4,
  color: ATARDECER.tinta,
  animation: `${asoma} .4s ease 1.6s both`,
  '::before': {
    content: '""',
    position: 'absolute',
    top: -6,
    left: 22,
    width: 12,
    height: 12,
    background: '#fff',
    transform: 'rotate(45deg)',
    borderRadius: 2,
  },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const estiloSello = css({
  position: 'absolute',
  right: -14,
  bottom: -12,
  width: 52,
  height: 52,
  borderRadius: '50%',
  border: `2.5px solid ${ATARDECER.rojo}`,
  color: ATARDECER.rojo,
  fontSize: 8,
  fontWeight: 800,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  display: 'grid',
  placeItems: 'center',
  textAlign: 'center',
  background: 'rgba(255,250,240,.6)',
  transform: 'rotate(-10deg)',
  animation: `${sella} .32s cubic-bezier(.3,1.6,.5,1) 1.1s both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const estiloEyebrow = {
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: '.16em',
  textTransform: 'uppercase',
  color: ATARDECER.latonOscuro,
} as const;

const estiloTitulo = {
  margin: '7px 0 0',
  fontFamily: 'Fraunces, Georgia, "Times New Roman", serif',
  fontSize: 24,
  fontWeight: 600,
  lineHeight: 1.15,
  color: ATARDECER.tinta,
} as const;

const estiloTexto = {
  margin: '8px 0 0',
  fontSize: 14,
  lineHeight: 1.45,
  color: ATARDECER.tinta2,
} as const;

const estiloBotonVerde = {
  width: '100%',
  maxWidth: 360,
  height: 50,
  border: 0,
  borderRadius: 999,
  background: ATARDECER.verde,
  color: '#fff',
  fontSize: 15,
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
  boxShadow: '0 8px 18px -10px rgba(31,143,92,.9)',
  ':active': { transform: 'scale(.97)' },
  ':focus-visible': {
    outline: `2px solid ${ATARDECER.verde}`,
    outlineOffset: 3,
  },
  ':disabled': { opacity: 0.75, cursor: 'wait' },
} as const;
